import { IssueCaseCategory, IssueCaseSubcategory } from '@gooduncles/gu-app-schema';
import { Alert, Checkbox, Form, Input, Modal, Select, message } from 'antd';
import { FC, useCallback, useMemo, useState } from 'react';
import { useAuth } from 'src/stores/auth-context';
import { ProductWithOrder } from 'src/stores/useBulkSoldOutIssueModal';

import { issueCaseOptions, issueCaseSubOptions } from 'src/utils/issue/issue-util';
import { createOrderProductIssue } from 'src/utils/order-product-issue-util';

import classes from './BulkSoldOutIssueModal.module.scss';

export interface BulkSoldOutIssueModalProps {
  open: boolean;
  onClose: () => void;
  products: ProductWithOrder[];
  source: string | null;
  /** 이슈 생성 완료 후 실제 품절 처리를 수행하는 콜백 */
  onConfirm: (params: { sendKakao: boolean }) => Promise<void>;
}

const BulkSoldOutIssueModal: FC<BulkSoldOutIssueModalProps> = ({ open, onClose, products, source, onConfirm }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState<IssueCaseCategory | null>(IssueCaseCategory.ORDER);
  const [subcategory, setSubcategory] = useState<IssueCaseSubcategory | null>(IssueCaseSubcategory.ORDER_ADJUSTMENT);
  const [memo, setMemo] = useState('');
  const [sendKakao, setSendKakao] = useState(true);

  const subcategoryOptions = useMemo(() => {
    if (!category) return null;
    return issueCaseSubOptions(category);
  }, [category]);

  const handleCategoryChange = useCallback((value: IssueCaseCategory) => {
    setCategory(value);
    setSubcategory(null);
  }, []);

  const handleClose = useCallback(() => {
    setCategory(IssueCaseCategory.ORDER);
    setSubcategory(IssueCaseSubcategory.ORDER_ADJUSTMENT);
    setMemo('');
    setSendKakao(true);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (!user) {
      message.error('사용자 정보가 없습니다. 다시 로그인 해주세요.');
      return;
    }

    if (!category) {
      message.error('이슈 카테고리를 선택해주세요.');
      return;
    }

    setLoading(true);
    try {
      // 1. 각 상품별 IssueCase + IssueTask 생성
      for (const productWithOrder of products) {
        const { order, ...product } = productWithOrder;
        await createOrderProductIssue({
          order,
          store: {
            storeId: order.storeId,
            storeName: order.store?.storeNickname ?? order.store?.storeName ?? '',
          },
          product,
          user,
          category,
          subcategory,
          memo,
          changeType: 'sold_out',
          source: source ?? undefined,
        });
      }

      // 2. 실제 품절 처리 + 카카오 발송
      await onConfirm({ sendKakao });

      handleClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [user, category, subcategory, memo, sendKakao, products, source, onConfirm, handleClose]);

  return (
    <Modal
      title='복수 상품 품절 처리'
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText='확인'
      cancelText='취소'
      width={480}
      okButtonProps={{ disabled: !category }}
    >
      <Form layout='vertical' className={classes.form}>
        <Alert
          type='info'
          message={
            <div className={classes.alertContent}>
              <strong>품절 처리 대상: {products.length}건</strong>
              <ul className={classes.productList}>
                {products.map((p) => (
                  <li key={`${p.order._id}-${p.productId}`}>
                    {p.fullName} ({p.order.store?.storeNickname ?? p.order.store?.storeName})
                  </li>
                ))}
              </ul>
            </div>
          }
          className={classes.alert}
        />

        <Form.Item label='분류' required tooltip='이슈 발생 원인을 분류합니다' className={classes.formItem}>
          <Select
            value={category}
            onChange={handleCategoryChange}
            placeholder='카테고리를 선택하세요'
            options={issueCaseOptions}
            className={classes.select}
          />
        </Form.Item>

        {subcategoryOptions && (
          <Form.Item label='상세 분류' tooltip='상세 분류를 선택합니다 (선택사항)' className={classes.formItem}>
            <Select
              value={subcategory}
              onChange={setSubcategory}
              placeholder='서브카테고리를 선택하세요'
              options={subcategoryOptions}
              className={classes.select}
              allowClear
            />
          </Form.Item>
        )}

        <Form.Item label='메모' tooltip='내부 관리용 메모입니다 (선택사항)' className={classes.formItem}>
          <Input.TextArea
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder='이슈에 대한 메모를 입력하세요 (선택)'
            rows={3}
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Form.Item className={classes.formItem}>
          <Checkbox checked={sendKakao} onChange={(e) => setSendKakao(e.target.checked)}>
            자동 카카오 알림톡 발송
          </Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default BulkSoldOutIssueModal;
